import { REGISTRY_BLOCK, type LayoutBlock } from '@web-studio/shared-schemas';
import { defaultContactFields } from '../lib/defaultFormFields';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../lib/imageProxy';
import { pickBlockField } from '../lib/localized';
import { parseFormFields } from '../lib/parseFormFields';
import { SITE_IMAGE_SIZES } from '../lib/responsiveImageSizes';
import { safeMailtoHref, safeMapEmbedUrl, safeTelHref } from '../lib/safeGuestLinks';
import { pickChrome } from '../lib/siteChromeCopy';
import { contactHoursLine, contactLandmark } from '../lib/contactSummary';
import { parseHoursSchedule } from '../lib/splitHoursLines';
import {
  LUCIDE_SSR_MAIL,
  LUCIDE_SSR_MAP_PIN,
  LUCIDE_SSR_PHONE,
  resolveSectionSsrIcon,
  type LucideSsrIcon,
} from '../icons/lucideSsrIconNodes';
import { renderLucideIconHtml } from '../icons/renderIconHtml';
import { escapeHtml } from './escape';
import { renderFormInput, renderLeadFormOpenTag, resolveLeadFormKey } from './leadFormHtml';

function icon(mark: LucideSsrIcon, className: string): string {
  return renderLucideIconHtml(mark.iconNode, { size: 'sm', className, name: mark.name });
}

function contactFormHtml(block: LayoutBlock, locale: string, panelClass: string): string {
  const formHeading = pickBlockField(block.content, 'formHeading', locale);
  const formSupporting = pickBlockField(block.content, 'formSupporting', locale);
  const consent = pickBlockField(block.content, 'consent', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const operational = parseFormFields(pickBlockField(block.content, 'formFields', locale));
  const consentFromFields = operational.find((field) => field.name === 'consent');
  const formFields =
    operational.length > 0
      ? operational.filter((field) => field.name !== 'consent')
      : defaultContactFields(locale);
  const inputs = formFields.map((field) => renderFormInput(field, 'ws-form__field', locale)).join('');
  const consentLabel = escapeHtml(
    consentFromFields?.label || consent || pickChrome('consentFallback', locale),
  );
  const submitLabel = escapeHtml(ctaLabel || pickChrome('submitFallbackReservation', locale));
  const statusIdle = escapeHtml(pickChrome('formStatusIdle', locale));
  const formOpen = renderLeadFormOpenTag({
    className: 'ws-contact__form',
    formKey: resolveLeadFormKey(block, 'contact'),
    locale,
  });
  return `<div class="${panelClass}">
      ${formHeading ? `<h3 class="ws-contact__form-title">${escapeHtml(formHeading)}</h3>` : ''}
      ${formSupporting ? `<span class="ws-contact__form-support">${escapeHtml(formSupporting)}</span>` : ''}
      ${formOpen}
        ${inputs}
        <label class="ws-form__consent"><input type="checkbox" name="consent" required /><span>${consentLabel}</span></label>
        <button type="submit" class="ws-contact__submit">${submitLabel}</button>
        <p class="ws-form__status" role="status">${statusIdle}</p>
      </form>
    </div>`;
}

function contactPhotoHtml(
  block: LayoutBlock,
  locale: string,
  visualClass: string,
  photoClass: string,
): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const address = pickBlockField(block.content, 'address', locale);
  const mapEmbedUrl = safeMapEmbedUrl(pickBlockField(block.content, 'mapEmbedUrl', locale));
  const mapImage = block.media.mapImage;
  const mapImageSrc = mapImage ? buildSiteImageProxyUrl(mapImage, 1200, 78) : '';
  const mapImageSrcSet = mapImage ? buildSiteImageSrcSet(mapImage, [480, 768, 1200], 78) : '';
  if (mapEmbedUrl) {
    return `<div class="${visualClass}"><iframe class="${photoClass}" src="${escapeHtml(mapEmbedUrl)}" title="${escapeHtml(sectionTitle || 'Map')}" loading="lazy" referrerpolicy="no-referrer-when-downgrade" allowfullscreen></iframe></div>`;
  }
  if (mapImageSrc) {
    return `<div class="${visualClass}"><img class="${photoClass}" src="${escapeHtml(mapImageSrc)}" srcset="${escapeHtml(mapImageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.contactMap)}" alt="${escapeHtml(address || sectionTitle || 'Map')}" width="1200" height="800" loading="lazy" decoding="async" /></div>`;
  }
  return '';
}

export function renderContactVisitHtml(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'sectionIcon', locale),
    LUCIDE_SSR_MAP_PIN,
  );
  const intro = pickBlockField(block.content, 'intro', locale);
  const address = pickBlockField(block.content, 'address', locale);
  const phone = pickBlockField(block.content, 'phone', locale);
  const hours = pickBlockField(block.content, 'hours', locale);
  const hoursLine = contactHoursLine(parseHoursSchedule(hours), hours);
  const telHref = safeTelHref(phone);
  const sectionIconHtml = renderLucideIconHtml(sectionIcon.iconNode, {
    size: 'lg',
    className: 'ws-contact__icon',
    name: sectionIcon.name,
  });
  const visualHtml = contactPhotoHtml(block, locale, 'ws-contact__visual', 'ws-contact__photo');
  const phoneHtml = phone
    ? telHref
      ? `<a class="ws-contact__call" href="${escapeHtml(telHref)}">${icon(LUCIDE_SSR_PHONE, 'ws-contact__action-icon')}<span>${escapeHtml(phone)}</span></a>`
      : `<span class="ws-contact__call">${escapeHtml(phone)}</span>`
    : '';
  return `<section class="ws-contact" data-block-id="${escapeHtml(block.blockId)}" data-component="Contact_Block" data-contact-arrangement="visit" id="visit">
  <div class="ws-contact__inner">
    <header class="ws-contact__header">
      <div class="ws-contact__title-row">
        <span class="ws-contact__icon-hit">${sectionIconHtml}</span>
        ${sectionTitle ? `<h2 class="ws-contact__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      </div>
      ${intro ? `<span class="ws-contact__intro">${escapeHtml(intro)}</span>` : ''}
    </header>
    ${visualHtml}
    ${address ? `<span class="ws-contact__caption">${escapeHtml(address)}</span>` : ''}
    <div class="ws-contact__actions">
      ${phoneHtml}
      ${hoursLine ? `<span class="ws-contact__open">${escapeHtml(hoursLine)}</span>` : ''}
    </div>
    ${contactFormHtml(block, locale, 'ws-contact__form-panel')}
  </div>
</section>`;
}

export function renderContactDeskHtml(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const intro = pickBlockField(block.content, 'intro', locale);
  const address = pickBlockField(block.content, 'address', locale);
  const phone = pickBlockField(block.content, 'phone', locale);
  const email = pickBlockField(block.content, 'email', locale);
  const hours = pickBlockField(block.content, 'hours', locale);
  const hoursLine = contactHoursLine(parseHoursSchedule(hours), hours);
  const telHref = safeTelHref(phone);
  const mailHref = safeMailtoHref(email);
  const visualHtml = contactPhotoHtml(block, locale, 'ws-contact-desk__visual', 'ws-contact__photo');
  const phoneHtml = phone
    ? telHref
      ? `<a class="ws-contact-desk__action" href="${escapeHtml(telHref)}">${icon(LUCIDE_SSR_PHONE, 'ws-contact__action-icon')}<span>${escapeHtml(phone)}</span></a>`
      : `<span class="ws-contact-desk__action">${escapeHtml(phone)}</span>`
    : '';
  const emailHtml = email
    ? mailHref
      ? `<a class="ws-contact-desk__action" href="${escapeHtml(mailHref)}">${icon(LUCIDE_SSR_MAIL, 'ws-contact__action-icon')}<span>${escapeHtml(email)}</span></a>`
      : `<span class="ws-contact-desk__action">${escapeHtml(email)}</span>`
    : '';
  const place =
    visualHtml || address
      ? `<div class="ws-contact-desk__place">${visualHtml}${address ? `<span class="ws-contact-desk__address">${escapeHtml(address)}</span>` : ''}</div>`
      : '';
  return `<section class="ws-contact-desk" data-block-id="${escapeHtml(block.blockId)}" data-component="Contact_Block" data-contact-arrangement="desk" id="visit">
  <div class="ws-contact-desk__inner">
    <header class="ws-contact-desk__header">
      ${sectionTitle ? `<h2 class="ws-contact-desk__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      ${intro ? `<span class="ws-contact-desk__intro">${escapeHtml(intro)}</span>` : ''}
    </header>
    ${contactFormHtml(block, locale, 'ws-contact__form-panel')}
    <ul class="ws-contact-desk__strip">
      ${phoneHtml ? `<li>${phoneHtml}</li>` : ''}
      ${emailHtml ? `<li>${emailHtml}</li>` : ''}
      ${hoursLine ? `<li><span class="ws-contact-desk__today">${escapeHtml(hoursLine)}</span></li>` : ''}
    </ul>
    ${place}
  </div>
</section>`;
}

export function renderContactLedgerHtml(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const intro = pickBlockField(block.content, 'intro', locale);
  const sectionIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'sectionIcon', locale),
    LUCIDE_SSR_MAP_PIN,
  );
  const address = pickBlockField(block.content, 'address', locale);
  const directions = pickBlockField(block.content, 'directions', locale);
  const phone = pickBlockField(block.content, 'phone', locale);
  const hours = pickBlockField(block.content, 'hours', locale);
  const rows = parseHoursSchedule(hours);
  const telHref = safeTelHref(phone);
  const sectionIconHtml = renderLucideIconHtml(sectionIcon.iconNode, {
    size: 'lg',
    className: 'ws-contact__icon',
    name: sectionIcon.name,
  });
  const phoneHtml = phone
    ? telHref
      ? `<a class="ws-contact-ledger__phone" href="${escapeHtml(telHref)}">${escapeHtml(phone)}</a>`
      : escapeHtml(phone)
    : '';
  const hoursHtml =
    rows.length > 0
      ? `<div class="ws-hours-schedule">${rows
          .map(
            (row) =>
              `<div class="ws-hours-schedule__row"><span class="ws-hours-schedule__day">${escapeHtml(row.day)}</span><span class="ws-hours-schedule__time">${escapeHtml(row.time)}</span></div>`,
          )
          .join('')}</div>`
      : hours
        ? escapeHtml(hours)
        : '';
  return `<section class="ws-contact-ledger" data-block-id="${escapeHtml(block.blockId)}" data-component="Contact_Block" data-contact-arrangement="ledger" id="visit">
  <div class="ws-contact-ledger__inner">
    <div class="ws-contact-ledger__house">
      <header class="ws-contact-ledger__header">
        <span class="ws-contact__icon-hit">${sectionIconHtml}</span>
        ${sectionTitle ? `<h2 class="ws-contact-ledger__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      </header>
      ${intro ? `<span class="ws-contact-ledger__intro">${escapeHtml(intro)}</span>` : ''}
      <div class="ws-contact-ledger__card">
        ${address ? `<span class="ws-contact-ledger__line ws-contact-ledger__line--address">${escapeHtml(address)}</span>` : ''}
        ${directions ? `<span class="ws-contact-ledger__line ws-contact-ledger__line--way">${escapeHtml(directions)}</span>` : ''}
        ${phoneHtml ? `<div class="ws-contact-ledger__line">${phoneHtml}</div>` : ''}
        ${hoursHtml ? `<div class="ws-contact-ledger__hours">${hoursHtml}</div>` : ''}
      </div>
    </div>
    ${contactFormHtml(block, locale, 'ws-contact-ledger__message')}
  </div>
</section>`;
}

export function renderContactThresholdHtml(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const intro = pickBlockField(block.content, 'intro', locale);
  const directions = pickBlockField(block.content, 'directions', locale);
  const phone = pickBlockField(block.content, 'phone', locale);
  const hours = pickBlockField(block.content, 'hours', locale);
  const rows = parseHoursSchedule(hours);
  const hoursLine = contactHoursLine(rows, hours);
  const landmark = contactLandmark(directions);
  const telHref = safeTelHref(phone);
  const visualHtml =
    contactPhotoHtml(block, locale, 'ws-contact-threshold__visual', 'ws-contact-threshold__photo') ||
    '<div class="ws-contact-threshold__visual ws-contact-threshold__visual--empty" aria-hidden="true"></div>';
  const phoneHtml = phone
    ? telHref
      ? `<a class="ws-contact-threshold__chip" href="${escapeHtml(telHref)}">${icon(LUCIDE_SSR_PHONE, 'ws-contact__action-icon')}<span>${escapeHtml(phone)}</span></a>`
      : `<span class="ws-contact-threshold__chip">${escapeHtml(phone)}</span>`
    : '';
  return `<section class="ws-contact-threshold" data-block-id="${escapeHtml(block.blockId)}" data-component="Contact_Block" data-contact-arrangement="threshold" id="visit">
  <div class="ws-contact-threshold__stage">
    ${visualHtml}
    <div class="ws-contact-threshold__scrim">
      ${sectionTitle ? `<h2 class="ws-contact-threshold__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      <ul class="ws-contact-threshold__chips">
        ${phoneHtml ? `<li>${phoneHtml}</li>` : ''}
        ${hoursLine ? `<li><span class="ws-contact-threshold__chip">${escapeHtml(hoursLine)}</span></li>` : ''}
        ${landmark ? `<li><span class="ws-contact-threshold__chip">${escapeHtml(landmark)}</span></li>` : ''}
      </ul>
    </div>
  </div>
  <div class="ws-contact-threshold__sheet">
    <div class="ws-contact__form-panel ws-contact-threshold__card">
      ${intro ? `<span class="ws-contact-threshold__intro">${escapeHtml(intro)}</span>` : ''}
      ${contactFormHtml(block, locale, 'ws-contact-threshold__message')}
    </div>
  </div>
</section>`;
}

const P = REGISTRY_BLOCK.presentation;

function withLocale(
  render: (block: LayoutBlock, locale: string) => string,
): (block: LayoutBlock, ctx: { locale: string }) => string {
  return (block, ctx) => render(block, ctx.locale);
}

export const CONTACT_SSR_REGISTRY_OVERRIDES = {
  [P.contactVisit]: withLocale(renderContactVisitHtml),
  [P.contactDesk]: withLocale(renderContactDeskHtml),
  [P.contactLedger]: withLocale(renderContactLedgerHtml),
  [P.contactThreshold]: withLocale(renderContactThresholdHtml),
};
